import { Search } from 'lucide-react';
import LocationSearch from 'components/location-search';
import MapFiltersPanel from 'components/search/map/map-filters-panel';
import type { Filter } from 'contexts/preferences/listing-params/types';
import type SearchPrediction from 'data/search-predictions';
import type { PartialDeep } from 'type-fest';
import type { ReactNode } from 'react';
import { useLocationSearchBar } from './use-location-search-bar';
import styles from './mobile-top-bar.module.scss';

export type MobileTopBarProps = {
  areaName?: string;
  filter: Filter;
  onLocationSearchClick: (searchPrediction: SearchPrediction) => void;
  onFiltersValueChange: (filters: PartialDeep<Filter>, changedFilter?: string) => void;
  filtersResultsCount?: number;
  /** Called whenever the mobile filters drawer opens or closes. */
  onFiltersOpenChange?: (isOpen: boolean) => void;
  /**
   * Replaces the location input with a trigger that expands the search bar into
   * the smart search panel. The panel itself is rendered by the caller.
   */
  isSmartSearchEnabled?: boolean;
  isSmartSearchOpen?: boolean;
  /** Text shown in the collapsed trigger, i.e. the current smart search query. */
  smartSearchQuery?: string;
  onSmartSearchOpen?: () => void;
  /** Expanded panel, rendered full-width below the search field while open. */
  smartSearchPanel?: ReactNode;
  /** Optional tenant-specific action rendered after the filters control. */
  trailingAction?: ReactNode;
};

/**
 * Mobile equivalent of the desktop `TopBar`'s search bar + filters row — same
 * `LocationSearch`/`MapFiltersPanel` reuse, laid out as a full-width flex row
 * instead of the desktop's absolutely-centered cluster.
 *
 * When smart search is enabled the location input is swapped for a trigger that
 * expands into the caller-provided panel, docked full-width across the screen.
 */
export function MobileTopBar({
  areaName,
  filter,
  onLocationSearchClick,
  onFiltersValueChange,
  filtersResultsCount,
  onFiltersOpenChange,
  isSmartSearchEnabled = false,
  isSmartSearchOpen = false,
  smartSearchQuery,
  onSmartSearchOpen,
  smartSearchPanel,
  trailingAction,
}: MobileTopBarProps) {
  const {
    locationQuery,
    setLocationQuery,
    searchBarPlaceholder,
    isLocationDropdownActive,
    setIsLocationDropdownActive,
    handleLocationSearchClick,
    handleSearchIconClick,
    searchContainerRef,
  } = useLocationSearchBar({ areaName, onLocationSearchClick });

  // While smart search is open the panel is the search field, so hide the
  // filters pill under it — same as the location dropdown already does.
  const isFiltersHidden = isLocationDropdownActive || isSmartSearchOpen;

  return (
    <>
      <div
        className={isSmartSearchOpen ? `${styles.search} ${styles.searchExpanded}` : styles.search}
        ref={searchContainerRef}
        data-testid="AreaPageV2-MobileTopBar-Search"
      >
        {!isSmartSearchOpen && (
          <Search className={styles.searchLeadingIcon} strokeWidth={2} aria-hidden />
        )}
        {isSmartSearchEnabled ? (
          // Hidden while expanded: the panel below takes over as the one and
          // only search field, rather than shadowing this one.
          !isSmartSearchOpen && (
            <button
              type="button"
              className={styles.smartSearchTrigger}
              onClick={onSmartSearchOpen}
              aria-expanded={isSmartSearchOpen}
              data-testid="AreaPageV2-MobileTopBar-SmartSearchTrigger"
            >
              {smartSearchQuery || (
                <span className={styles.smartSearchPlaceholder}>
                  {searchBarPlaceholder
                    ? `Search ${searchBarPlaceholder}`
                    : 'Search'}
                </span>
              )}
            </button>
          )
        ) : (
          <LocationSearch
            locationQuery={locationQuery}
            setLocationQuery={setLocationQuery}
            isLocationDropdownActive={isLocationDropdownActive}
            setIsLocationDropdownActive={setIsLocationDropdownActive}
            onClick={handleLocationSearchClick}
            placeholder={searchBarPlaceholder || 'Search'}
            onSearchIconClick={handleSearchIconClick}
            className={styles.locationSearch}
          />
        )}
        {smartSearchPanel && (
          <div className={styles.smartSearchDock}>{smartSearchPanel}</div>
        )}
      </div>
      {!isFiltersHidden && (
        <MapFiltersPanel
          filters={filter}
          onValueChange={onFiltersValueChange}
          resultsCount={filtersResultsCount}
          className={styles.filtersPanel}
          variant="floating"
          onOpenChange={onFiltersOpenChange}
        />
      )}
      {!isFiltersHidden && trailingAction && (
        <div className={styles.trailingAction}>{trailingAction}</div>
      )}
    </>
  );
}
